import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import moment from "moment-timezone";
import { useLiffProfile } from "../../context/LiffProfileContext";
import ProfileHeader from "../header/ProfileHeader";
import LoadingBar from "../LoadingBar";
import { MaskInputHelper } from "../helpers/mask-input-helper";

// Filter tabs shown at the top of the Point History page.
const FILTERS = [
  { key: "all", label: "point_history_filter_all" },
  { key: "earn", label: "point_history_filter_earn" },
  { key: "use", label: "point_history_filter_use" },
];

// Number of recent months available in the month picker / default range.
const MONTHS_BACK = 12;

// Thai shows Thai month names + Buddhist Era year (e.g. "มิถุนายน 2569");
// English shows Gregorian (e.g. "June 2026"). Mirrors the project's existing helper.
const intlFmt = (iso, isTH, opts) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(isTH ? "th-TH-u-ca-buddhist" : "en-US", {
      timeZone: "Asia/Bangkok",
      ...opts,
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

const fmtMonthYear = (iso, isTH) =>
  intlFmt(iso, isTH, { month: "long", year: "numeric" });

const fmtDateTime = (iso, isTH) =>
  intlFmt(iso, isTH, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

// Fixed-label transaction types (Big Point History - Transaction Type Record).
const LABEL_KEY_BY_TYPE = {
  RV: "ph_type_rv", // Reversal -> ยกเลิกใบเสร็จ / Order Cancelled
  PC: "ph_type_pc", // Points Corrections -> อัพเดตบิ๊กพอยต์ / Points Adjustment
  BR: "ph_type_br", // Redemption -> แลกพอยต์ / Redeemed
  RF: "ph_type_rf", // Redemption Refund -> ยกเลิกแลกพอยต์ / Redemption Cancelled
  CR: "ph_type_cr", // Customer Registration -> สมัครสมาชิก / Joined Membership
  FC: "ph_type_fc", // Food Court -> ฟู้ดพาร์ค / Food Park
  GM: "ph_type_gm", // Gamification -> รางวัลจากกิจกรรม / Game Reward
  BT: "ph_type_bt", // Bottle recycle -> ขวดแลกพอยต์ / Bottle Recycle
  AY: "ph_type_account_adjust", // System Points Transfer+ -> ปรับปรุงบัญชี / Account Adjustment
  PT: "ph_type_account_adjust", // System Point Transfer -> ปรับปรุงบัญชี / Account Adjustment
};

// Resolves the display name shown to the user, per the agreed transaction-type table.
const getTransactionLabel = (item, t) => {
  const type = (item.type || "").toUpperCase();
  const partner = item.partnerName || "";

  // ER (Sale) shows the partner/merchant name directly.
  if (type === "ER") return partner || item.typeName || t("ph_type_other");

  // EP (Exchange Point): earned vs transferred out, by sign.
  if (type === "EP") {
    return (item.points ?? 0) >= 0
      ? t("ph_type_ep_earn", { partner })
      : t("ph_type_ep_transfer", { partner });
  }

  // Point Expiration (type PE) -> พอยต์หมดอายุ. Also match by name as a fallback.
  if (type === "PE" || /expir/i.test(item.typeName || "")) return t("ph_type_expiration");

  const key = LABEL_KEY_BY_TYPE[type];
  return key ? t(key) : t("ph_type_other");
};

const PointHistoryItem = ({ item, isTH, seniorLayout, t }) => {
  const earned = (item.points ?? 0) >= 0;
  const sign = earned ? "+" : "-";
  const amount = MaskInputHelper.formatNumber(Math.abs(item.points ?? 0));
  // Only receipt-based transactions show a reference line; trnNo is never shown.
  const reference = item.receiptNo || "";
  const title = getTransactionLabel(item, t);

  return (
    <div className="w-full flex items-center justify-between px-5 py-3 bg-white border-b border-gray-100">
      <div className="flex flex-col text-left min-w-0">
        <div
          className={`font-semibold text-primary-text truncate ${
            seniorLayout ? "text-xl" : "text-base"
          }`}
        >
          {title || "-"}
        </div>
        {reference && (
          <div
            className={`text-gray-400 truncate ${
              seniorLayout ? "text-base" : "text-sm"
            }`}
          >
            {reference}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end pl-3 shrink-0">
        <div
          className={`font-semibold ${earned ? "text-[#3aaa35]" : "text-danger"} ${
            seniorLayout ? "text-2xl" : "text-lg"
          }`}
        >
          {sign}
          {amount}
        </div>
        <div
          className={`text-gray-400 ${seniorLayout ? "text-base" : "text-xs"}`}
        >
          {fmtDateTime(item.date, isTH)}
        </div>
      </div>
    </div>
  );
};

const PointHistory = () => {
  const { t, i18n } = useTranslation();
  const { authToken, userId } = useLiffProfile();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  // YYYY-MM string, or "" for the default (last 3 months) range.
  const [selectedMonth, setSelectedMonth] = useState("");

  const [seniorLayout] = useState(
    () => localStorage.getItem("seniorLayout") === "true"
  );

  const isTH = (i18n.language || "en").toLowerCase().startsWith("th");

  // Build the list of recent months for the picker.
  const monthOptions = useMemo(() => {
    const opts = [];
    for (let i = 0; i < MONTHS_BACK; i += 1) {
      const m = moment().tz("Asia/Bangkok").subtract(i, "month");
      const value = m.format("YYYY-MM");
      // Use mid-month so the Bangkok->UTC conversion never shifts the month label.
      const labelIso = moment.tz(`${value}-15`, "YYYY-MM-DD", "Asia/Bangkok").toISOString();
      opts.push({ value, label: fmtMonthYear(labelIso, isTH) });
    }
    return opts;
  }, [isTH]);

  useEffect(() => {
    if (!authToken || !userId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = {
          Authorization: `Bearer ${authToken}`,
          "X-Client-Auth": process.env.REACT_APP_CLIENT_AUTH,
          "x-user-id": userId,
          "Accept-Language": i18n.language.toUpperCase(),
        };

        const params = {};
        if (selectedMonth) {
          const start = moment.tz(selectedMonth, "YYYY-MM", "Asia/Bangkok").startOf("month");
          const end = moment.tz(selectedMonth, "YYYY-MM", "Asia/Bangkok").endOf("month");
          params.dateFrom = start.toISOString();
          params.dateTo = end.toISOString();
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/current/clm-point-history`,
          { headers, params }
        );
        // Defensive: hide zero-point rows even if the API build hasn't been redeployed yet.
        setItems((response.data?.data || []).filter((it) => (it.points ?? 0) !== 0));
      } catch (error) {
        console.error("Error fetching point history:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authToken, userId, i18n.language, selectedMonth]);

  // Apply the earn/use tab filter.
  const filteredItems = useMemo(() => {
    if (activeFilter === "earn") return items.filter((it) => (it.points ?? 0) >= 0);
    if (activeFilter === "use") return items.filter((it) => (it.points ?? 0) < 0);
    return items;
  }, [items, activeFilter]);

  // Group the visible rows by month for section headers (matches the design).
  const groupedByMonth = useMemo(() => {
    const groups = [];
    const indexByKey = {};
    filteredItems.forEach((it) => {
      const key = it.date
        ? moment(it.date).tz("Asia/Bangkok").format("YYYY-MM")
        : "unknown";
      if (indexByKey[key] === undefined) {
        indexByKey[key] = groups.length;
        groups.push({
          key,
          label: fmtMonthYear(it.date, isTH),
          items: [],
        });
      }
      groups[indexByKey[key]].items.push(it);
    });
    return groups;
  }, [filteredItems, isTH]);

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="h-screen flex flex-col bg-secondary-background-gray">
        {/* Same header as My Coupons */}
        <ProfileHeader className="bg-primary" />

        <div className="flex-grow overflow-y-auto">
          {/* Title + filter pills (My Coupons theme) */}
          <div className="w-full flex flex-col gap-y-4 bg-secondary-background py-5 sm:py-4">
            <div
              className={`w-full px-5 font-semibold text-left ${
                seniorLayout ? "text-2xl" : "text-lg"
              }`}
            >
              {t("point_history")}
            </div>

            <div className="w-full min-h-[2.5rem] px-2 sm:px-0 flex justify-between text-lg sm:text-base font-medium overflow-x-auto no-scrollbars">
              {FILTERS.map((f) => (
                <div
                  key={f.key}
                  className={`h-10 flex-1 px-5 flex justify-center items-center rounded-full cursor-pointer whitespace-nowrap ${
                    activeFilter === f.key ? "bg-primary" : ""
                  }`}
                  onClick={() => setActiveFilter(f.key)}
                >
                  {t(f.label)}
                </div>
              ))}
            </div>

            {/* Range / month picker */}
            <div className="w-full flex flex-col px-5 gap-1">
              {/* Row 1: label (left) + month dropdown (right) */}
              <div className="w-full flex items-center justify-between">
                <span
                  className={`text-secondary-text-gray ${
                    seniorLayout ? "text-base" : "text-sm"
                  }`}
                >
                  {t("point_history_showing")}
                </span>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className={`bg-white border border-gray-300 rounded-full px-3 py-1 text-secondary-text-gray ${
                    seniorLayout ? "text-base" : "text-sm"
                  }`}
                >
                  <option value="">{t("point_history_recent")}</option>
                  {monthOptions.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* Row 2: selected date range */}
              {/*<span
                className={`w-full text-left text-secondary-text-gray ${
                  seniorLayout ? "text-base" : "text-sm"
                }`}
              >
                {rangeLabel}
              </span>*/}
            </div>
          </div>

          {/* List */}
          <div className="w-full flex flex-col bg-secondary-background-gray">
            {!loading && groupedByMonth.length === 0 ? (
              <div className="w-full flex flex-col items-center justify-center text-center px-6 py-20 gap-2">
                <div
                  className={`font-semibold text-gray-600 ${
                    seniorLayout ? "text-xl" : "text-base"
                  }`}
                >
                  {t("point_history_empty_title")}
                </div>
                <div
                  className={`text-gray-400 ${
                    seniorLayout ? "text-base" : "text-sm"
                  }`}
                >
                  {t("point_history_empty_desc")}
                </div>
              </div>
            ) : (
              groupedByMonth.map((group) => (
                <div key={group.key} className="w-full">
                  <div
                    className={`px-5 py-2 bg-secondary-background-gray text-secondary-text-gray text-left ${
                      seniorLayout ? "text-base" : "text-sm"
                    }`}
                  >
                    {group.label}
                  </div>
                  {group.items.map((item, idx) => (
                    <PointHistoryItem
                      key={`${item.transactionId ?? item.trnNo ?? "x"}-${idx}`}
                      item={item}
                      isTH={isTH}
                      seniorLayout={seniorLayout}
                      t={t}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <LoadingBar loading={loading} label="Loading..." />
    </div>
  );
};

export default PointHistory;
